@file:NoLiveLiterals

package com.example.borshi.web.data

import androidx.compose.runtime.NoLiveLiterals
import com.example.borshi.web.firebase.FirebaseService
import com.example.borshi.web.firebase.FirebaseFirestore
import com.example.borshi.web.model.ReferralItem
import com.example.borshi.web.model.ReferralStatsData
import com.example.borshi.web.model.ReferralStatus
import kotlinx.coroutines.await
import kotlin.js.Promise
import kotlin.coroutines.resume
import kotlin.coroutines.resumeWithException
import kotlin.coroutines.suspendCoroutine

/**
 * Repository for managing referral operations in the web app
 * Integrates with Firebase Firestore for data persistence
 */
class ReferralRepository(private val firebaseService: FirebaseService) {

    /** Returns the current uid, or throws "User not logged in" */
    private fun requireUid(): String {
        firebaseService.init()
        val uid = firebaseService.getCurrentUserId()
        if (uid.isNullOrBlank()) throw Exception("User not logged in")
        return uid
    }

    private fun db() = firebaseService.getFirestore()

    /**
     * Get current user's referral code from their profile
     */
    suspend fun getMyReferralCode(): Result<String?> = runCatching {
        val uid = firebaseService.getCurrentUserId() ?: return@runCatching null
        val userRef = FirebaseFirestore.doc(db(), "users", uid)
        val userSnap = FirebaseFirestore.getDoc(userRef).await()
        if (!userSnap.exists()) return@runCatching null
        userSnap.data()?.referralCode as? String
    }

    /**
     * Validate a referral code
     */
    suspend fun validateReferralCode(code: String): Result<Boolean> = runCatching {
        if (code.length < 9) {
            console.log("Referral code too short: $code")
            return@runCatching false
        }

        val db = db()
        val codeRef = FirebaseFirestore.doc(db, "referralCodes", code)
        val codeSnap = FirebaseFirestore.getDoc(codeRef).await()
        if (!codeSnap.exists()) {
            console.log("Referral code not found: $code")
            return@runCatching false
        }

        val referrerId = codeSnap.data()?.userId as? String
        if (referrerId == null) {
            console.log("Referral code has no userId: $code")
            return@runCatching false
        }

        val referrerRef = FirebaseFirestore.doc(db, "users", referrerId)
        val referrerSnap = FirebaseFirestore.getDoc(referrerRef).await()
        if (!referrerSnap.exists()) {
            console.log("Referrer user not found: $referrerId")
            return@runCatching false
        }

        val hasMadePurchase = referrerSnap.data()?.hasMadePurchase as? Boolean ?: false
        if (!hasMadePurchase) {
            console.log("Referrer has not made a purchase yet: $referrerId")
        }
        hasMadePurchase
    }

    /**
     * Get referral statistics for current user
     */
    suspend fun getReferralStats(): Result<ReferralStatsData> = runCatching {
        val uid = requireUid()
        val db = db()

        val userRef = FirebaseFirestore.doc(db, "users", uid)
        val userSnap = FirebaseFirestore.getDoc(userRef).await()
        if (!userSnap.exists()) return@runCatching ReferralStatsData()
        val userData = userSnap.data()

        val referralsQuery = FirebaseFirestore.query(
            FirebaseFirestore.collection(db, "referrals"),
            FirebaseFirestore.where("referrerId", "==", uid)
        )
        val snapshot = FirebaseFirestore.getDocs(referralsQuery).await()
        val docs = snapshot.docs.unsafeCast<Array<dynamic>>()

        var pendingCount = 0
        var completedCount = 0
        var flaggedCount = 0
        docs.forEach { doc ->
            when (doc.data()?.status as? String) {
                "completed" -> completedCount++
                "pending"   -> pendingCount++
                "flagged"   -> flaggedCount++
            }
        }

        ReferralStatsData(
            totalReferrals = userData?.referralCount as? Int ?: completedCount,
            totalBonus = userData?.referralBonus as? Double ?: 0.0,
            referralCode = userData?.referralCode as? String ?: "",
            pendingReferrals = pendingCount,
            completedReferrals = completedCount,
            flaggedReferrals = flaggedCount
        )
    }

    /**
     * Get referral history for current user
     */
    suspend fun getReferralHistory(): Result<List<ReferralItem>> = runCatching {
        val uid = firebaseService.getCurrentUserId() ?: return@runCatching emptyList()
        val db = db()

        val referralsQuery = FirebaseFirestore.query(
            FirebaseFirestore.collection(db, "referrals"),
            FirebaseFirestore.where("referrerId", "==", uid),
            FirebaseFirestore.orderBy("createdAt", "desc")
        )
        val snapshot = FirebaseFirestore.getDocs(referralsQuery).await()
        val docs = snapshot.docs.unsafeCast<Array<dynamic>>()

        docs.map { doc ->
            val data = doc.data()
            ReferralItem(
                id = doc.id as? String ?: "",
                referrerId = data?.referrerId as? String ?: "",
                refereeId  = data?.refereeId  as? String ?: "",
                status = ReferralStatus.fromString(data?.status as? String),
                createdAt   = getTimestampMillis(data?.createdAt),
                completedAt = if (data?.completedAt != null) getTimestampMillis(data.completedAt) else null,
                rewardAmount = data?.rewardAmount as? Double ?: 100.0
            )
        }.toList()
    }

    /**
     * Create user with referral code during signup
     */
    suspend fun createUserWithReferralCode(
        email: String,
        password: String,
        name: String,
        phone: String,
        referralCode: String?
    ): Result<Unit> = runCatching {
        // Auth creation still needs the compat SDK path here; delegate to FirebaseService sign-up
        val db = db()
        val uid = firebaseService.getCurrentUserId()
            ?: throw Exception("Failed to create user - no UID returned")

        val userData = js("({})")
        userData.uid   = uid
        userData.email = email
        userData.name  = name
        userData.phone = phone

        if (!referralCode.isNullOrEmpty()) {
            userData.referredBy = referralCode.uppercase().trim()
            console.log("Signup with referral code: $referralCode")
        }

        val docRef = FirebaseFirestore.doc(db, "users", uid)
        FirebaseFirestore.setDoc(docRef, userData).await()
        console.log("User document created successfully")
    }

    /**
     * Check if current user can refer others (has made a purchase)
     */
    suspend fun canReferOthers(): Result<Boolean> = runCatching {
        val uid = firebaseService.getCurrentUserId() ?: return@runCatching false
        val docRef = FirebaseFirestore.doc(db(), "users", uid)
        val snap = FirebaseFirestore.getDoc(docRef).await()
        if (!snap.exists()) return@runCatching false
        snap.data()?.hasMadePurchase as? Boolean ?: false
    }

    /**
     * Listen to real-time updates for user's referral stats
     */
    fun listenToReferralUpdates(userId: String, onUpdate: (ReferralStatsData) -> Unit): () -> Unit {
        val docRef = FirebaseFirestore.doc(db(), "users", userId)
        return FirebaseFirestore.onSnapshot(docRef) { snapshot ->
            if (snapshot.exists()) {
                val data = snapshot.data()
                val updatedStats = ReferralStatsData(
                    totalReferrals = data?.referralCount as? Int ?: 0,
                    totalBonus     = data?.referralBonus as? Double ?: 0.0,
                    referralCode   = data?.referralCode  as? String ?: ""
                )
                onUpdate(updatedStats)
            }
        }
    }

    private fun getTimestampMillis(timestamp: dynamic): Long {
        return try {
            when {
                timestamp == null -> 0L
                js("typeof timestamp.toMillis === 'function'").unsafeCast<Boolean>() ->
                    timestamp.toMillis().unsafeCast<Long>()
                js("typeof timestamp.seconds === 'number'").unsafeCast<Boolean>() ->
                    timestamp.seconds.unsafeCast<Long>() * 1000
                else -> 0L
            }
        } catch (e: Exception) {
            console.error("Error converting timestamp:", e)
            0L
        }
    }
}

/**
 * Extension function to await JavaScript Promises (kept for any external callers)
 */
suspend fun <T> Promise<T>.awaitPromise(): T = suspendCoroutine { continuation ->
    this.then(
        { result -> continuation.resume(result) },
        { error -> continuation.resumeWithException(Exception("Promise rejected: $error")) }
    )
}

